import _extends from "@babel/runtime/helpers/extends";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["id", "inputRef", "value", "label", "labelProps", "isSearching", "withBorder", "transparent", "onClear", "onFocus", "onBlur", "wrapperRef", "wrapperProps"];
import "core-js/modules/es.regexp.exec.js";
import "core-js/modules/es.string.search.js";
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';
import queueFunctions from 'utils/queueFunctions';
import { pxToRem } from 'utils/ui/units';
import { alpha } from 'utils/ui/colors';
import { spacing } from 'styles/globals';
import { spin } from 'styles/animations';
import { textInputReset } from 'components/TextInput';
import Icon from 'components/Icon';
import Button from 'components/Button';
import VisuallyHidden from 'components/VisuallyHidden';
import useUniqueId from 'hooks/useUniqueId';
var Wrapper = styled.div.withConfig({
  displayName: "SearchBox__Wrapper",
  componentId: "sc-8k5vaw-0"
})(["display:flex;align-items:center;height:", ";margin:0;padding:0;padding-left:", ";background-color:", ";border:1px solid transparent;border-radius:", ";cursor:text;transition:all 0.25s ease-in;", " ", " ", ""], pxToRem(36), spacing.xs, function (p) {
  return alpha(p.theme.shade, p.theme.shadeStrength);
}, function (p) {
  return pxToRem(p.theme.globals.borderRadius.search);
}, function (p) {
  return p.withBorder && css(["border-color:", ";"], function (p) {
    return alpha(p.theme.shade, p.theme.lineStrength);
  });
}, function (p) {
  return p.isFocused && css(["border-color:", ";box-shadow:0 3px 0 ", ";transition-timing-function:ease-out;"], function (p) {
    return p.theme.links;
  }, function (p) {
    return p.theme.links;
  });
}, function (p) {
  return p.transparent && css(["background-color:transparent;border-color:transparent;"]);
});
var InputWrapper = styled.div.withConfig({
  displayName: "SearchBox__InputWrapper",
  componentId: "sc-8k5vaw-1"
})(["flex:1 1 auto;"]);
var Input = styled.input.withConfig({
  displayName: "SearchBox__Input",
  componentId: "sc-8k5vaw-2"
})(["", " width:100%;padding:0 ", ";transform:translateY(", ");color:", ";&::placeholder{color:", ";}&::-ms-clear,&::-ms-reveal{display:none;width:0;height:0;}"], textInputReset, spacing.xs, pxToRem(1), function (p) {
  return p.theme.text;
}, function (p) {
  return alpha(p.theme.text, p.theme.textDimStrength);
});
var ClearButtonWrapper = styled.div.withConfig({
  displayName: "SearchBox__ClearButtonWrapper",
  componentId: "sc-8k5vaw-3"
})(["transition:opacity 0.25s ease-out,visibility 0s ease-out;", ""], function (p) {
  return p.isHidden && css(["visibility:hidden;opacity:0;transition-delay:0.25s,0s;"]);
});
var ClearButton = styled(Button).withConfig({
  displayName: "SearchBox__ClearButton",
  componentId: "sc-8k5vaw-4"
})(["vertical-align:middle;border-radius:", ";"], function (p) {
  return pxToRem(p.theme.globals.borderRadius.search);
});
var SpinningIcon = styled(Icon).withConfig({
  displayName: "SearchBox__SpinningIcon",
  componentId: "sc-8k5vaw-5"
})(["animation:", " 0.6s infinite linear both;"], spin);
function SearchBox(props) {
  var id = props.id,
    inputRef = props.inputRef,
    value = props.value,
    label = props.label,
    labelProps = props.labelProps,
    isSearching = props.isSearching,
    withBorder = props.withBorder,
    transparent = props.transparent,
    onClear = props.onClear,
    onFocus = props.onFocus,
    onBlur = props.onBlur,
    wrapperRef = props.wrapperRef,
    wrapperProps = props.wrapperProps,
    otherProps = _objectWithoutProperties(props, _excluded);
  var _useTranslation = useTranslation(),
    t = _useTranslation.t;
  var uniqueId = useUniqueId(id);
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    isFocused = _useState2[0],
    setFocus = _useState2[1];
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(VisuallyHidden, _extends({
    as: "label",
    htmlFor: uniqueId
  }, labelProps), label || t('actions_search')), /*#__PURE__*/React.createElement(Wrapper, _extends({
    ref: wrapperRef
  }, wrapperProps, {
    transparent: transparent,
    withBorder: withBorder,
    isFocused: isFocused
  }), isSearching ? /*#__PURE__*/React.createElement(SpinningIcon, {
    name: "spinnerdot",
    size: "medium"
  }) : /*#__PURE__*/React.createElement(Icon, {
    name: "search",
    size: "medium"
  }), /*#__PURE__*/React.createElement(InputWrapper, null, /*#__PURE__*/React.createElement(Input, _extends({
    type: "text",
    id: uniqueId,
    ref: inputRef,
    autoComplete: "off",
    value: value || '',
    onFocus: queueFunctions(function () {
      return setFocus(true);
    }, onFocus),
    onBlur: queueFunctions(function () {
      return setFocus(false);
    }, onBlur)
  }, otherProps))), onClear && /*#__PURE__*/React.createElement(ClearButtonWrapper, {
    isHidden: !value
  }, /*#__PURE__*/React.createElement(ClearButton, {
    size: "medium",
    icon: "x",
    variant: "transparent",
    onClick: onClear,
    "aria-label": t('actions_clear_search')
  }))));
}
export default SearchBox;